import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Tabs,
  DatePicker,
  Button,
  Space,
  Table,
  Tag,
  Popconfirm,
  App,
  Input,
  InputNumber,
  Dropdown,
} from 'antd'
import {
  TableOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
  DownOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { formatDisplayDateTime, formatOnChainLabel, isOnChained } from '../../utils/format'
import { formatDocumentStatus } from '../../utils/documentStatus'
import { dataSourcesApi, dataSourceFilterSchemesApi } from '../../services/api'
import Permission from '../../components/Permission'
import AdvancedQueryModal, { emptyAdvancedQuery } from './AdvancedQueryModal'
import ExportFieldModal from './ExportFieldModal'
import type { AdvancedQueryPayload, FilterFormSnapshot, SavedFilterScheme } from './savedFilterSchemes'
import { saveDataListCache, loadDataListCache, parseCachedDayRange } from '../../utils/dataListCache'

const { RangePicker } = DatePicker

const statusTextMap: Record<string, string> = {
  available: '可用',
  void: '作废',
  written_off: '已核销',
  used: '已使用',
}

const statusColorMap: Record<string, string> = {
  available: 'green',
  void: 'red',
  written_off: 'orange',
  used: 'blue',
}

/** 列表行（与后端字段兼容映射）；金额缺省为 null，表格显示「-」 */
export type DataRow = {
  key: string
  id: string
  name: string
  source: string
  type: string
  documentStatus: string
  /** 上链状态：接口 onChain「是/否」→ 已上链/未上链 */
  onChain: string
  contractNo: string
  contractType: string
  customer: string
  settlementOrg: string
  productName: string
  priceTaxTotal: number | null
  receiptAmount: number | null
  partialReceipt: string
  unreceivedAmount: number | null
  invoicedAmount: number | null
  contractCreatedDate: string
  contractReceiptDate: string
  value: number | null
  status: string
  createdTime: string
  /** POST /api/data-sources/contract-chain/upload */
  ordernumber: string
  bill_no: string
  /** 已上链时不展示「上链」操作 */
  onChainListed: boolean
  contractAttachment: string
  orderAttachment: string
  receiptAttachment: string
  logisticsAttachment: string
  invoiceAttachment: string
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '' || v === '-') return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  return n
}

function pickListOrdernumber(item: Record<string, unknown>): string {
  return String(
    item.ordernumber ??
      item.orderNumber ??
      item.order_number ??
      item.orderNo ??
      item.order_no ??
      item.FOrderNo ??
      item.forder_no ??
      '',
  ).trim()
}

function pickListBillNo(item: Record<string, unknown>): string {
  return String(
    item.bill_no ??
      item.billNo ??
      item.BillNo ??
      item.documentNo ??
      item.document_no ??
      item.billNumber ??
      item.bill_number ??
      item.billno ??
      item.contractNo ??
      item.contract_no ??
      item.contractNumber ??
      '',
  ).trim()
}

/** 空值统一为「-」（与接口 list 中空字段约定一致） */
function strOrDash(...vals: unknown[]): string {
  for (const x of vals) {
    if (x != null && x !== '') return String(x)
  }
  return '-'
}

function deriveUnreceived(item: Record<string, unknown>): number | null {
  const fromApi = numOrNull(item.unreceivedAmount ?? item.unreceived_amount)
  if (fromApi !== null) return Math.max(0, fromApi)
  const pt = numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount)
  if (pt === null) return null
  const ra = numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount) ?? 0
  return Math.max(0, pt - ra)
}

const AMOUNT_EPS = 0.01

function derivePartialReceipt(item: Record<string, unknown>): string {
  const fromApi = strOrDash(item.partialReceipt, item.partial_receipt)
  if (fromApi !== '-') return fromApi
  const pt = numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount)
  if (pt === null || pt <= AMOUNT_EPS) return '-'
  const ra = numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount) ?? 0
  if (ra <= AMOUNT_EPS || ra >= pt - AMOUNT_EPS) return '否'
  return '是'
}

function normalizeDataList(res: any): DataRow[] {
  const raw = res?.data
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.list)
      ? raw.list
      : Array.isArray(raw)
        ? raw
        : Array.isArray(res?.list)
          ? res.list
          : Array.isArray(res)
            ? res
            : []
  return list.map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? item.data_id ?? '',
    name: item.name ?? item.data_name ?? '',
    source: item.source ?? '',
    type: item.type ?? '应收',
    documentStatus: (() => {
      const fromApi = strOrDash(item.documentStatus, item.document_status)
      if (fromApi !== '-') return fromApi
      const resolved = formatDocumentStatus(item as Record<string, unknown>)
      return resolved !== '-' ? resolved : '-'
    })(),
    onChain: (() => {
      const label = formatOnChainLabel(item.onChain ?? item.on_chain)
      if (label) return label
      if (
        isOnChained(item.onChain ?? item.on_chain) ||
        String(item.documentStatus ?? item.document_status ?? '').trim() === '已上链'
      ) {
        return '已上链'
      }
      return '-'
    })(),
    contractNo: strOrDash(item.contractNo, item.contract_no, item.contractNumber),
    contractType: strOrDash(item.contractType, item.contract_type),
    customer: strOrDash(item.customer, item.customerName),
    settlementOrg: strOrDash(item.settlementOrg, item.settlement_org, item.orgName),
    productName: strOrDash(item.productName, item.product_name, item.goodsName, item.goods_name),
    priceTaxTotal: numOrNull(item.priceTaxTotal ?? item.price_tax_total ?? item.totalAmount ?? item.amount),
    receiptAmount: numOrNull(item.receiptAmount ?? item.receipt_amount ?? item.receivedAmount),
    partialReceipt: derivePartialReceipt(item as Record<string, unknown>),
    unreceivedAmount: deriveUnreceived(item as Record<string, unknown>),
    invoicedAmount: numOrNull(item.invoicedAmount ?? item.invoiced_amount ?? item.invoiceAmount),
    contractCreatedDate: strOrDash(
      item.contractCreatedDate,
      item.contract_created_date,
      item.contractCreatedAt,
      item.contract_created_at
    ),
    contractReceiptDate: strOrDash(
      item.contractReceiptDate,
      item.contract_receipt_date,
      item.receiptDate,
      item.receipt_date
    ),
    value: numOrNull(item.value ?? item.amount),
    status: item.status ?? item.assetStatus ?? 'available',
    createdTime: strOrDash(
      item.createdTime,
      item.created_at,
      item.createdDate,
      item.create_time,
      item.created_date
    ),
    ordernumber: pickListOrdernumber(item as Record<string, unknown>),
    bill_no: pickListBillNo(item as Record<string, unknown>),
    onChainListed:
      isOnChained(item.onChain ?? item.on_chain) ||
      String(item.documentStatus ?? item.document_status ?? '').trim() === '已上链',
    contractAttachment: strOrDash(item.contractAttachment, item.contract_attachment),
    orderAttachment: strOrDash(item.orderAttachment, item.order_attachment),
    receiptAttachment: strOrDash(item.receiptAttachment, item.receipt_attachment),
    logisticsAttachment: strOrDash(item.logisticsAttachment, item.logistics_attachment),
    invoiceAttachment: strOrDash(item.invoiceAttachment, item.invoice_attachment),
  }))
}

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`
  return v
}

function rowsToCsv(rows: DataRow[]): string {
  const headers = [
    '数据项ID',
    '数据名称',
    '来源',
    '类型',
    '单据状态',
    '上链状态',
    '合同号',
    '合同类型',
    '客户',
    '结算组织',
    '商品名称',
    '价税合计',
    '收款金额',
    '部分收款',
    '应收账款资产', // 暂时隐藏
    '已开票额',
    '合同创建日期',
    '合同收款日期',
    '状态',
    '创建时间',
  ]
  const lines = [headers.join(',')]
  for (const r of rows) {
    lines.push(
      [
        r.id,
        r.name,
        r.source,
        r.type,
        r.documentStatus,
        r.onChain,
        r.contractNo,
        r.contractType,
        r.customer,
        r.settlementOrg,
        r.productName,
        r.priceTaxTotal == null ? '-' : String(r.priceTaxTotal),
        r.receiptAmount == null ? '-' : String(r.receiptAmount),
        r.partialReceipt,
        r.unreceivedAmount == null ? '-' : String(r.unreceivedAmount), // 暂时隐藏
        r.invoicedAmount == null ? '-' : String(r.invoicedAmount),
        r.contractCreatedDate,
        r.contractReceiptDate,
        statusTextMap[r.status] ?? r.status,
        r.createdTime,
      ]
        .map((c) => escapeCsvCell(String(c ?? '')))
        .join(',')
    )
  }
  return '\uFEFF' + lines.join('\n')
}

function triggerDownloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function parseDayRangeTuple(t: [string | null, string | null] | null): [Dayjs | null, Dayjs | null] | null {
  if (!t?.[0] || !t?.[1]) return null
  const a = dayjs(t[0])
  const b = dayjs(t[1])
  return a.isValid() && b.isValid() ? [a, b] : null
}

function formatRangeTuple(r: [Dayjs | null, Dayjs | null] | null): [string | null, string | null] | null {
  if (!r?.[0] || !r?.[1]) return null
  return [r[0].format('YYYY-MM-DD'), r[1].format('YYYY-MM-DD')]
}

const DataVisualization = () => {
  const { message, modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('table')
  const [assetType, setAssetType] = useState('all')
  const [assetStatus, setAssetStatus] = useState('all')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedAssetType, setAppliedAssetType] = useState('all')
  const [appliedAssetStatus, setAppliedAssetStatus] = useState('all')
  const [appliedDateRange, setAppliedDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [appliedSource, setAppliedSource] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [appliedCustomer, setAppliedCustomer] = useState('')
  const [filterSettlementOrg, setFilterSettlementOrg] = useState('')
  const [appliedSettlementOrg, setAppliedSettlementOrg] = useState('')
  const [contractAmountMin, setContractAmountMin] = useState<number | null>(null)
  const [contractAmountMax, setContractAmountMax] = useState<number | null>(null)
  const [appliedContractMin, setAppliedContractMin] = useState<number | null>(null)
  const [appliedContractMax, setAppliedContractMax] = useState<number | null>(null)
  const [contractCreatedRange, setContractCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedContractCreatedRange, setAppliedContractCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(
    null
  )
  const [receiptDateRange, setReceiptDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedReceiptDateRange, setAppliedReceiptDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [appliedAdvancedQuery, setAppliedAdvancedQuery] = useState<AdvancedQueryPayload>(() => emptyAdvancedQuery())
  const [advancedModalInitialQuery, setAdvancedModalInitialQuery] = useState<AdvancedQueryPayload>(() =>
    emptyAdvancedQuery()
  )
  const [advancedModalOpen, setAdvancedModalOpen] = useState(false)
  const [savedSchemes, setSavedSchemes] = useState<SavedFilterScheme[]>([])

  const [tableData, setTableData] = useState<DataRow[]>([])
  const [listTotal, setListTotal] = useState(0)
  const [listPage, setListPage] = useState(1)
  const [listPageSize, setListPageSize] = useState(10)
  const [dataLoading, setDataLoading] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryStats, setSummaryStats] = useState<{
    totalValue: number
    totalUnreceivedAmount: number
    availableValue: number
    totalCount: number
    availableCount: number
  } | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [exporting, setExporting] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [pendingExportMode, setPendingExportMode] = useState<'filtered' | 'selected'>('filtered')
  const skipNextListFetch = useRef(false)

  const listFiltersPayload = useMemo(() => {
    const filters: Record<string, unknown> = {}
    if (appliedAssetType !== 'all') filters.type = appliedAssetType
    if (appliedAssetStatus !== 'all') filters.status = appliedAssetStatus
    if (appliedKeyword.trim()) filters.keyword = appliedKeyword.trim()
    if (appliedDateRange?.[0] && appliedDateRange?.[1]) {
      filters.dateFrom = appliedDateRange[0].format('YYYY-MM-DD')
      filters.dateTo = appliedDateRange[1].format('YYYY-MM-DD')
    }
    if (appliedSource.trim()) filters.source = appliedSource.trim()
    if (appliedCustomer.trim()) filters.customer = appliedCustomer.trim()
    if (appliedSettlementOrg.trim()) filters.settlementOrg = appliedSettlementOrg.trim()
    if (appliedContractMin != null) filters.contractTotalAmountMin = appliedContractMin
    if (appliedContractMax != null) filters.contractTotalAmountMax = appliedContractMax
    if (appliedContractCreatedRange?.[0] && appliedContractCreatedRange?.[1]) {
      filters.contractCreatedFrom = appliedContractCreatedRange[0].format('YYYY-MM-DD')
      filters.contractCreatedTo = appliedContractCreatedRange[1].format('YYYY-MM-DD')
    }
    if (appliedReceiptDateRange?.[0] && appliedReceiptDateRange?.[1]) {
      filters.receiptDateFrom = appliedReceiptDateRange[0].format('YYYY-MM-DD')
      filters.receiptDateTo = appliedReceiptDateRange[1].format('YYYY-MM-DD')
    }
    const hasAdvanced = appliedAdvancedQuery.groups.some((g) =>
      g.rows.some((r) => r.field && r.operator && String(r.value ?? '').trim() !== '')
    )
    if (hasAdvanced) filters.advancedQuery = appliedAdvancedQuery
    return filters
  }, [
    appliedAssetType,
    appliedAssetStatus,
    appliedKeyword,
    appliedDateRange,
    appliedSource,
    appliedCustomer,
    appliedSettlementOrg,
    appliedContractMin,
    appliedContractMax,
    appliedContractCreatedRange,
    appliedReceiptDateRange,
    appliedAdvancedQuery,
  ])

  const buildSnapshot = (): FilterFormSnapshot => ({
    assetType,
    assetStatus,
    keyword: keywordInput,
    filterSource,
    filterCustomer,
    filterSettlementOrg,
    contractAmountMin,
    contractAmountMax,
    contractCreatedRange: formatRangeTuple(contractCreatedRange),
    receiptDateRange: formatRangeTuple(receiptDateRange),
    dateRange: formatRangeTuple(dateRange),
  })

  const applySnapshot = (s: FilterFormSnapshot) => {
    setAssetType(s.assetType)
    setAssetStatus(s.assetStatus)
    setKeywordInput(s.keyword)
    setFilterSource(s.filterSource)
    setFilterCustomer(s.filterCustomer)
    setFilterSettlementOrg(s.filterSettlementOrg)
    setContractAmountMin(s.contractAmountMin)
    setContractAmountMax(s.contractAmountMax)
    setContractCreatedRange(parseDayRangeTuple(s.contractCreatedRange))
    setReceiptDateRange(parseDayRangeTuple(s.receiptDateRange))
    setDateRange(parseDayRangeTuple(s.dateRange))
    setAppliedAssetType(s.assetType)
    setAppliedAssetStatus(s.assetStatus)
    setAppliedKeyword(s.keyword.trim())
    setAppliedSource(s.filterSource.trim())
    setAppliedCustomer(s.filterCustomer.trim())
    setAppliedSettlementOrg(s.filterSettlementOrg.trim())
    setAppliedContractMin(s.contractAmountMin)
    setAppliedContractMax(s.contractAmountMax)
    setAppliedContractCreatedRange(parseDayRangeTuple(s.contractCreatedRange))
    setAppliedReceiptDateRange(parseDayRangeTuple(s.receiptDateRange))
    setAppliedDateRange(parseDayRangeTuple(s.dateRange))
  }

  const refreshSavedSchemes = useCallback(async () => {
    try {
      const res = (await dataSourceFilterSchemesApi.list({})) as {
        data?: { items?: SavedFilterScheme[] }
        items?: SavedFilterScheme[]
      }
      const items = res?.data?.items ?? res?.items
      if (Array.isArray(items)) {
        setSavedSchemes(items as SavedFilterScheme[])
        return
      }
      setSavedSchemes([])
    } catch {
      setSavedSchemes([])
    }
  }, [])

  useEffect(() => {
    void refreshSavedSchemes()
  }, [refreshSavedSchemes])

  const fetchSummary = useCallback(() => {
    setSummaryLoading(true)
    const body: Record<string, unknown> = {
      filters: { ...listFiltersPayload },
    }
    dataSourcesApi
      .summary(body)
      .then((res: any) => {
        const d = res?.data
        if (d && (typeof d.totalValue === 'number' || typeof d.totalCount === 'number')) {
          const clamp = (n: unknown) => {
            const x = Number(n ?? 0)
            return Number.isFinite(x) && x >= 0 ? x : 0
          }
          setSummaryStats({
            totalValue: clamp(d.totalValue),
            totalUnreceivedAmount: clamp(d.totalUnreceivedAmount),
            availableValue: clamp(d.availableValue ?? d.activeValue),
            totalCount: clamp(d.totalCount),
            availableCount: clamp(d.availableCount ?? d.activeCount),
          })
        } else {
          setSummaryStats(null)
        }
      })
      .catch(() => setSummaryStats(null))
      .finally(() => setSummaryLoading(false))
  }, [listFiltersPayload])

  const fetchList = useCallback(() => {
    setDataLoading(true)
    const filters = { ...listFiltersPayload }
    const body: Record<string, unknown> = {
      page: listPage,
      pageSize: listPageSize,
    }
    if (Object.keys(filters).length) body.filters = filters
    dataSourcesApi
      .list(body)
      .then((res: any) => {
        const rows = normalizeDataList(res)
        const data = res?.data
        setTableData(rows.length ? rows : [])
        setListTotal(Number(data?.total ?? data?.totalCount ?? rows.length))
      })
      .catch(() => {
        setTableData([])
        setListTotal(0)
      })
      .finally(() => setDataLoading(false))
  }, [listPage, listPageSize, listFiltersPayload])

  useEffect(() => {
    const state = location.state as { restoreListCache?: boolean } | null
    if (state?.restoreListCache) {
      const cached = loadDataListCache()
      if (cached) {
        setTableData(cached.tableData as DataRow[])
        setListTotal(cached.listTotal)
        setListPage(cached.listPage)
        setListPageSize(cached.listPageSize)
        setSelectedRowKeys(cached.selectedRowKeys)
        setAppliedAssetType(cached.appliedAssetType)
        setAppliedAssetStatus(cached.appliedAssetStatus)
        setAppliedKeyword(cached.appliedKeyword)
        setKeywordInput(cached.appliedKeyword)
        setAppliedSource(cached.appliedSource)
        setFilterSource(cached.appliedSource)
        setAppliedCustomer(cached.appliedCustomer)
        setFilterCustomer(cached.appliedCustomer)
        setAppliedSettlementOrg(cached.appliedSettlementOrg)
        setFilterSettlementOrg(cached.appliedSettlementOrg)
        setAppliedContractMin(cached.appliedContractMin)
        setContractAmountMin(cached.appliedContractMin)
        setAppliedContractMax(cached.appliedContractMax)
        setContractAmountMax(cached.appliedContractMax)
        setAppliedContractCreatedRange(parseCachedDayRange(cached.appliedContractCreatedRange))
        setContractCreatedRange(parseCachedDayRange(cached.appliedContractCreatedRange))
        setAppliedReceiptDateRange(parseCachedDayRange(cached.appliedReceiptDateRange))
        setReceiptDateRange(parseCachedDayRange(cached.appliedReceiptDateRange))
        setAppliedDateRange(parseCachedDayRange(cached.appliedDateRange))
        setDateRange(parseCachedDayRange(cached.appliedDateRange))
        setAppliedAdvancedQuery(cached.appliedAdvancedQuery)
        setSummaryStats(cached.summaryStats)
        skipNextListFetch.current = true
        navigate(location.pathname, { replace: true, state: {} })
      }
    }
  }, [location.state, location.pathname, navigate])

  useEffect(() => {
    if (skipNextListFetch.current) {
      skipNextListFetch.current = false
      return
    }
    fetchList()
  }, [fetchList])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    const state = location.state as { searchKeyword?: string; highlightId?: string } | null
    const kw = state?.searchKeyword?.trim()
    const highlightId = state?.highlightId
    if (highlightId) {
      navigate(`/data/${encodeURIComponent(highlightId)}`, { replace: true, state: {} })
      return
    }
    if (kw) {
      setAppliedKeyword(kw)
      setKeywordInput(kw)
      setListPage(1)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  const displayData = tableData

  const handleRefresh = () => {
    setAssetType('all')
    setAssetStatus('all')
    setDateRange(null)
    setFilterSource('')
    setFilterCustomer('')
    setFilterSettlementOrg('')
    setContractAmountMin(null)
    setContractAmountMax(null)
    setContractCreatedRange(null)
    setReceiptDateRange(null)
    setAppliedAssetType('all')
    setAppliedAssetStatus('all')
    setAppliedDateRange(null)
    setAppliedKeyword('')
    setKeywordInput('')
    setAppliedSource('')
    setAppliedCustomer('')
    setAppliedSettlementOrg('')
    setAppliedContractMin(null)
    setAppliedContractMax(null)
    setAppliedContractCreatedRange(null)
    setAppliedReceiptDateRange(null)
    setAppliedAdvancedQuery(emptyAdvancedQuery())
    setAdvancedModalInitialQuery(emptyAdvancedQuery())
    setSelectedRowKeys([])
    setListPage(1)
    message.success('数据已刷新')
  }

  const handleFilter = () => {
    const kw = keywordInput.trim()
    // 场景卡片里的类型/状态/日期在页面上未展示，清空以免沿用常用筛选场景里的隐含条件
    setAssetType('all')
    setAssetStatus('all')
    setDateRange(null)
    setAppliedAssetType('all')
    setAppliedAssetStatus('all')
    setAppliedDateRange(null)
    setAppliedKeyword(kw)
    setAppliedSource(filterSource.trim())
    setAppliedCustomer(filterCustomer.trim())
    setAppliedSettlementOrg(filterSettlementOrg.trim())
    setAppliedContractMin(contractAmountMin)
    setAppliedContractMax(contractAmountMax)
    setAppliedContractCreatedRange(contractCreatedRange)
    setAppliedReceiptDateRange(receiptDateRange)
    // 仅按当前筛选区 + 关键词查询：清空常用筛选场景带入的高级查询
    setAppliedAdvancedQuery(emptyAdvancedQuery())
    setAdvancedModalInitialQuery(emptyAdvancedQuery())
    setListPage(1)
    message.success('已应用筛选条件')
  }

  const openAdvancedQueryModal = () => {
    setAdvancedModalInitialQuery(JSON.parse(JSON.stringify(appliedAdvancedQuery)) as AdvancedQueryPayload)
    setAdvancedModalOpen(true)
  }

  const handleAdvancedQueryApply = (query: AdvancedQueryPayload) => {
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(query)) as AdvancedQueryPayload)
    setListPage(1)
  }

  const handleSaveSchemeFromModal = async (name: string, query: AdvancedQueryPayload) => {
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `scheme_${Date.now()}`
    try {
      await dataSourceFilterSchemesApi.save({
        id,
        name,
        snapshot: buildSnapshot() as unknown as Record<string, unknown>,
        advancedQuery: JSON.parse(JSON.stringify(query)) as Record<string, unknown>,
      })
      await refreshSavedSchemes()
      message.success('已保存到服务端')
    } catch {
      message.error('保存场景失败，请稍后重试')
    }
  }

  const handleLoadSchemeFromModal = (scheme: SavedFilterScheme) => {
    applySnapshot(scheme.snapshot)
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(scheme.advancedQuery)) as AdvancedQueryPayload)
    setListPage(1)
    message.success(`已载入方案「${scheme.name}」`)
  }

  const handleApplySavedSchemeTag = (scheme: SavedFilterScheme) => {
    applySnapshot(scheme.snapshot)
    setAppliedAdvancedQuery(JSON.parse(JSON.stringify(scheme.advancedQuery)) as AdvancedQueryPayload)
    setListPage(1)
    message.success(`已应用场景「${scheme.name}」`)
  }

  const handleRemoveSavedScheme = async (id: string) => {
    try {
      await dataSourceFilterSchemesApi.delete({ id })
      await refreshSavedSchemes()
      message.success('已删除该场景')
    } catch {
      message.error('删除失败，请稍后重试')
    }
  }

  const handleOpenChain = (record: DataRow) => {
    const ordernumber = String(record.ordernumber ?? '').trim()
    const bill_no = String(record.bill_no ?? '').trim()
    if (!ordernumber || !bill_no) {
      message.warning('缺少订单号(ordernumber)或合同单号(bill_no)，无法提交上链')
      return
    }
    modal.confirm({
      title: '上链确认',
      content: `确认将数据项「${record.name}」（${record.id}）上链？提交后请等待链上确认。`,
      okText: '确认上链',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dataSourcesApi.contractChainUpload({ ordernumber, bill_no })
          message.success('已提交上链请求')
          fetchList()
          fetchSummary()
        } catch {
          message.error('上链请求失败')
          return Promise.reject(new Error('upload failed'))
        }
      },
    })
  }

  const handleDelete = (key: string) => {
    const record = tableData.find((item) => item.key === key)
    const id = record?.id ?? key
    dataSourcesApi
      .delete({ id })
      .then(() => {
        setTableData((prev) => prev.filter((item) => item.key !== key))
        message.success('数据项已删除')
      })
      .catch(() => {
        message.error('删除失败，请重试')
      })
  }

  const persistListCache = () => {
    saveDataListCache({
      tableData,
      listTotal,
      listPage,
      listPageSize,
      selectedRowKeys,
      appliedAssetType,
      appliedAssetStatus,
      appliedKeyword,
      appliedSource,
      appliedCustomer,
      appliedSettlementOrg,
      appliedContractMin,
      appliedContractMax,
      appliedContractCreatedRange: formatRangeTuple(appliedContractCreatedRange),
      appliedReceiptDateRange: formatRangeTuple(appliedReceiptDateRange),
      appliedDateRange: formatRangeTuple(appliedDateRange),
      appliedAdvancedQuery,
      summaryStats,
    })
  }

  const handleView = (record: DataRow) => {
    persistListCache()
    navigate(`/data/${encodeURIComponent(record.id)}`)
  }

  const selectedRows = useMemo(
    () => tableData.filter((r) => selectedRowKeys.includes(r.key)),
    [tableData, selectedRowKeys]
  )

  const openExportModal = (mode: 'filtered' | 'selected') => {
    if (mode === 'selected' && !selectedRows.length) {
      message.warning('请先勾选要导出的数据行')
      return
    }
    setPendingExportMode(mode)
    setExportModalOpen(true)
  }

  const runExport = async (mode: 'filtered' | 'selected', columns: string[]) => {
    if (mode === 'selected' && !selectedRows.length) {
      message.warning('请先勾选要导出的数据行')
      return
    }
    setExporting(true)
    try {
      const filters = { ...listFiltersPayload }
      const body: Record<string, unknown> = {
        mode,
        format: 'csv',
        filters,
        columns,
      }
      if (mode === 'selected') {
        body.ids = selectedRows.map((r) => r.id).filter(Boolean)
      }
      try {
        const res: unknown = await dataSourcesApi.export(body)
        if (res instanceof Blob) {
          if (res.type.includes('json')) {
            const text = await res.text()
            try {
              const j = JSON.parse(text) as { data?: { fileUrl?: string }; fileUrl?: string }
              const url = j?.data?.fileUrl ?? j?.fileUrl
              if (url) {
                window.open(url, '_blank')
                message.success('已打开导出文件链接')
                return
              }
            } catch {
              /* fall through */
            }
          } else {
            triggerDownloadBlob(res, `资产数据导出_${dayjs().format('YYYYMMDD_HHmmss')}.csv`)
            message.success('导出成功')
            return
          }
        }
      } catch {
        /* client fallback */
      }
      const rows = mode === 'selected' ? selectedRows : displayData
      if (!rows.length) {
        message.warning('当前没有可导出的数据')
        return
      }
      const csv = rowsToCsv(rows)
      triggerDownloadBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        `资产数据导出_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
      )
      message.success('已使用本地导出（接口不可用或返回非文件时）')
    } finally {
      setExporting(false)
    }
  }

  const money = (n: number) => `¥${Number(n ?? 0).toLocaleString()}`
  const moneyCell = (v: number | null | undefined) => {
    if (v == null) return '-'
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    return money(n)
  }

  /** 合同/订单/收款/物流/发票附件：完整 | 部分 | 缺失（兼容历史 有/没有） */
  const attachmentTag = (v: string) => {
    if (!v || v === '-') return '-'
    const colorMap: Record<string, string> = {
      完整: 'green',
      部分: 'orange',
      缺失: 'red',
      有: 'green',
      没有: 'default',
    }
    return <Tag color={colorMap[v] ?? 'default'}>{v}</Tag>
  }

  const columns = [
    {
      title: '合同附件',
      dataIndex: 'contractAttachment',
      key: 'contractAttachment',
      width: 90,
      render: (v: string) => attachmentTag(v),
    },
    {
      title: '订单附件',
      dataIndex: 'orderAttachment',
      key: 'orderAttachment',
      width: 90,
      render: (v: string) => attachmentTag(v),
    },
    {
      title: '收款附件',
      dataIndex: 'receiptAttachment',
      key: 'receiptAttachment',
      width: 90,
      render: (v: string) => attachmentTag(v),
    },
    {
      title: '物流附件',
      dataIndex: 'logisticsAttachment',
      key: 'logisticsAttachment',
      width: 90,
      render: (v: string) => attachmentTag(v),
    },
    {
      title: '发票附件',
      dataIndex: 'invoiceAttachment',
      key: 'invoiceAttachment',
      width: 90,
      render: (v: string) => attachmentTag(v),
    },
    { title: '数据项ID', dataIndex: 'id', key: 'id', width: 120, ellipsis: true },
    { title: '数据名称', dataIndex: 'name', key: 'name', width: 160, ellipsis: true },
    { title: '来源', dataIndex: 'source', key: 'source', width: 100, ellipsis: true },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (t: string) => <Tag color="blue">{t}</Tag>,
    },
    { title: '单据状态', dataIndex: 'documentStatus', key: 'documentStatus', width: 100, ellipsis: true },
    {
      title: '上链状态',
      dataIndex: 'onChain',
      key: 'onChain',
      width: 100,
      render: (v: string) => {
        if (v === '已上链') return <Tag color="green">已上链</Tag>
        if (v === '未上链') return <Tag color="default">未上链</Tag>
        return '-'
      },
    },
    { title: '合同号', dataIndex: 'contractNo', key: 'contractNo', width: 120, ellipsis: true },
    { title: '合同类型', dataIndex: 'contractType', key: 'contractType', width: 100, ellipsis: true },
    { title: '客户', dataIndex: 'customer', key: 'customer', width: 120, ellipsis: true },
    { title: '结算组织', dataIndex: 'settlementOrg', key: 'settlementOrg', width: 120, ellipsis: true },
    { title: '商品名称', dataIndex: 'productName', key: 'productName', width: 140, ellipsis: true },
    {
      title: '价税合计',
      dataIndex: 'priceTaxTotal',
      key: 'priceTaxTotal',
      width: 110,
      render: (_: unknown, row: DataRow) => moneyCell(row.priceTaxTotal),
    },
    {
      title: '收款金额',
      dataIndex: 'receiptAmount',
      key: 'receiptAmount',
      width: 110,
      render: (_: unknown, row: DataRow) => moneyCell(row.receiptAmount),
    },
    {
      title: '部分收款',
      dataIndex: 'partialReceipt',
      key: 'partialReceipt',
      width: 90,
      render: (v: string) => {
        if (!v || v === '-') return '-'
        return <Tag color={v === '是' ? 'orange' : 'default'}>{v}</Tag>
      },
    },
    {
      title: '未收款金额',
      dataIndex: 'unreceivedAmount',
      key: 'unreceivedAmount',
      width: 110,
      render: (_: unknown, row: DataRow) => moneyCell(row.unreceivedAmount),
    },
    {
      title: '已开票额',
      dataIndex: 'invoicedAmount',
      key: 'invoicedAmount',
      width: 110,
      render: (_: unknown, row: DataRow) => moneyCell(row.invoicedAmount),
    },
    { title: '合同创建日期', dataIndex: 'contractCreatedDate', key: 'contractCreatedDate', width: 120, ellipsis: true },
    { title: '合同收款日期', dataIndex: 'contractReceiptDate', key: 'contractReceiptDate', width: 120, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => <Tag color={statusColorMap[status]}>{statusTextMap[status] ?? status}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdTime',
      key: 'createdTime',
      width: 170,
      render: (t: string) => formatDisplayDateTime(t),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: unknown, record: DataRow) => (
        <Space size="small" wrap>
          <Button type="link" size="small" onClick={() => handleView(record)}>
            查看
          </Button>
          {!record.onChainListed ? (
            <Button type="link" size="small" onClick={() => handleOpenChain(record)}>
              上链
            </Button>
          ) : null}
          <Permission code="data_sources:delete">
            <Popconfirm
              title="确认删除该数据项？"
              description="删除后将无法恢复，请确认已完成备份。"
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record.key)}
            >
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          </Permission>
        </Space>
      ),
    },
  ]

  const exportMenuItems = [
    {
      key: 'filtered',
      label: '按当前筛选条件导出',
      onClick: () => openExportModal('filtered'),
    },
    {
      key: 'selected',
      label: '导出已选中数据',
      onClick: () => openExportModal('selected'),
    },
  ]

  const hasSummary = summaryStats != null
  const showVal = (val: number, formatter: (n: number) => string) => (hasSummary && val !== 0 ? formatter(val) : '')

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold mb-2">资产数据</h1>
          <p className="text-gray-600">统一管理与查看数字资产数据明细</p>
        </div>
        <Space wrap>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} loading={dataLoading}>
            刷新数据
          </Button>
        </Space>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">总资产价值</div>
          <div className="text-2xl font-semibold" style={{ color: '#3b82f6' }}>
            {showVal(summaryStats?.totalValue ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">应收账款资产</div>
          <div className="text-2xl font-semibold" style={{ color: '#ef4444' }}>
            {showVal(summaryStats?.totalUnreceivedAmount ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">可用资产价值</div>
          <div className="text-2xl font-semibold" style={{ color: '#10b981' }}>
            {showVal(summaryStats?.availableValue ?? 0, (n) => `¥${(n / 10000).toFixed(2)}万`)}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">资产数量总量</div>
          <div className="text-2xl font-semibold" style={{ color: '#8b5cf6' }}>
            {showVal(summaryStats?.totalCount ?? 0, (n) => String(n))}
          </div>
        </Card>
        <Card loading={summaryLoading}>
          <div className="text-gray-500 text-sm mb-1">可用资产数据总量</div>
          <div className="text-2xl font-semibold" style={{ color: '#f59e0b' }}>
            {showVal(summaryStats?.availableCount ?? 0, (n) => String(n))}
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
      <Card title="筛选条件">
        <div className="space-y-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">来源</div>
              <Input
                placeholder="来源系统 / 渠道"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">客户</div>
              <Input placeholder="客户名称" value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">结算组织</div>
              <Input
                placeholder="结算组织"
                value={filterSettlementOrg}
                onChange={(e) => setFilterSettlementOrg(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">合同总金额</div>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  style={{ width: '50%' }}
                  placeholder="最小"
                  min={0}
                  value={contractAmountMin ?? undefined}
                  onChange={(v) => setContractAmountMin(v == null ? null : Number(v))}
                />
                <InputNumber
                  style={{ width: '50%' }}
                  placeholder="最大"
                  min={0}
                  value={contractAmountMax ?? undefined}
                  onChange={(v) => setContractAmountMax(v == null ? null : Number(v))}
                />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">合同创建时间</div>
              <RangePicker
                style={{ width: '100%' }}
                value={contractCreatedRange}
                onChange={(d) => setContractCreatedRange(d as [Dayjs | null, Dayjs | null] | null)}
              />
            </Col>
            {/* 与合同创建时间同栅格宽度 lg=6，两个 RangePicker 在 100% 下等宽；按钮单独一列避免挤压日期 */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="text-xs text-gray-500 mb-1">收款日期</div>
              <RangePicker
                style={{ width: '100%' }}
                value={receiptDateRange}
                onChange={(d) => setReceiptDateRange(d as [Dayjs | null, Dayjs | null] | null)}
              />
            </Col>
            <Col xs={24} sm={24} md={8} lg={12} className="flex items-end">
              <Space wrap className="pb-px" size="small">
                <Button icon={<FilterOutlined />} type="primary" onClick={handleFilter}>
                  应用筛选
                </Button>
                <Button icon={<SearchOutlined />} onClick={openAdvancedQueryModal}>
                  高级查询
                </Button>
              </Space>
            </Col>
          </Row>
          <div className="pt-2 mt-2">
            <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
              <span className="text-sm font-medium text-gray-700 shrink-0 leading-[22px]">常用筛选场景：</span>
              {savedSchemes.length > 0 ? (
                <Space wrap className="flex-1 min-w-0">
                  {savedSchemes.map((s) => (
                    <Tag
                      key={s.id}
                      className="cursor-pointer py-0.5 px-2 text-sm"
                      onClick={() => handleApplySavedSchemeTag(s)}
                      closable
                      onClose={() => handleRemoveSavedScheme(s.id)}
                    >
                      {s.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span className="text-gray-400 text-sm leading-[22px]">暂无已保存场景，可在「高级查询」中保存方案</span>
              )}
            </div>
          </div>
          {/* 列表筛选模块暂时隐藏，仍保留状态供路由关键词等逻辑使用 */}
        </div>
      </Card>

      <Card
        title="资产数据列表"
        styles={{ body: { paddingTop: 12 } }}
        extra={
          <Permission codes={['data:export', 'data_sources:export', 'data_sources:list']}>
            <Dropdown menu={{ items: exportMenuItems }} disabled={exporting}>
              <Button type="primary" icon={<DownloadOutlined />} loading={exporting}>
                导出数据 <DownOutlined />
              </Button>
            </Dropdown>
          </Permission>
        }
      >
        <Tabs
          size="small"
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarStyle={{ marginBottom: 8 }}
          items={[
            {
              key: 'table',
              label: (
                <span>
                  <TableOutlined />
                  表格视图
                </span>
              ),
              children: (
                <Table
                  dataSource={displayData}
                  columns={columns}
                  loading={dataLoading}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    preserveSelectedRowKeys: true,
                  }}
                  pagination={{
                    current: listPage,
                    pageSize: listPageSize,
                    total: listTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (t) => `共 ${t} 条`,
                    onChange: (page, pageSize) => {
                      setListPage(page)
                      setListPageSize(pageSize || listPageSize)
                    },
                  }}
                  rowClassName="hover:bg-gray-50 cursor-pointer"
                  scroll={{ x: 3100 }}
                  onRow={(record) => ({
                    onDoubleClick: () => handleView(record),
                  })}
                />
              ),
            },
          ]}
        />
      </Card>
      </div>

      <AdvancedQueryModal
        open={advancedModalOpen}
        onClose={() => setAdvancedModalOpen(false)}
        initialQuery={advancedModalInitialQuery}
        onQuery={handleAdvancedQueryApply}
        schemes={savedSchemes}
        onSaveScheme={handleSaveSchemeFromModal}
        onLoadScheme={handleLoadSchemeFromModal}
      />
      <ExportFieldModal
        open={exportModalOpen}
        exporting={exporting}
        onCancel={() => setExportModalOpen(false)}
        onConfirm={(cols) => {
          setExportModalOpen(false)
          void runExport(pendingExportMode, cols)
        }}
      />
    </div>
  )
}

export default DataVisualization
